import * as types from '../mutation-types';
import WhitelabelAPI from '../../api/superAdmin/whitelabel';

const state = {
  config: {
    scripts: '',
    lastModified: null,
  },
  backups: [],
  auditLogs: [],
  credentialsStatus: {
    configured: false,
    provider: null,
    model: null,
    hasEncryptedKey: false,
    hasLegacyKey: false,
    maskedKey: null,
  },
  aiConfig: {
    model: 'claude-sonnet-4-5',
    apiKey: '',
    isConfigured: false,
  },
  analysis: null,
  chatMessages: [],
  uiFlags: {
    isFetching: false,
    isSaving: false,
    isAnalyzing: false,
    isTestingConnection: false,
    isFetchingBackups: false,
    isRestoring: false,
    isExporting: false,
    isImporting: false,
    isMigrating: false,
    isChatting: false,
  },
};

export const getters = {
  getConfig: $state => $state.config,
  getScripts: $state => $state.config.scripts || '',
  getBackups: $state => $state.backups,
  getAuditLogs: $state => $state.auditLogs,
  getCredentialsStatus: $state => $state.credentialsStatus,
  getAiConfig: $state => $state.aiConfig,
  getAnalysis: $state => $state.analysis,
  getChatMessages: $state => $state.chatMessages,
  getUIFlags: $state => $state.uiFlags,
  isAiConfigured: $state => $state.credentialsStatus.configured,
};

export const actions = {
  // Fetch current whitelabel config
  fetchConfig: async ({ commit }) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isFetching: true });
    try {
      const response = await WhitelabelAPI.getConfig();
      commit(types.default.SET_WHITELABEL_CONFIG, response.data);
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isFetching: false });
    }
  },

  // Save scripts
  saveScripts: async ({ commit }, scripts) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isSaving: true });
    try {
      const response = await WhitelabelAPI.saveScripts(scripts);
      commit(types.default.SET_WHITELABEL_SCRIPTS, scripts);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isSaving: false });
    }
  },

  // Fetch backups
  fetchBackups: async ({ commit }) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isFetchingBackups: true });
    try {
      const response = await WhitelabelAPI.getBackups();
      commit(types.default.SET_WHITELABEL_BACKUPS, response.data.backups || []);
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, {
        isFetchingBackups: false,
      });
    }
  },

  // Restore backup
  restoreBackup: async ({ commit, dispatch }, timestamp) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isRestoring: true });
    try {
      await WhitelabelAPI.restoreBackup(timestamp);
      await dispatch('fetchConfig');
      await dispatch('fetchBackups');
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isRestoring: false });
    }
  },

  // Analyze script with AI
  analyzeScript: async ({ commit }, scriptContent) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isAnalyzing: true });
    commit(types.default.SET_WHITELABEL_ANALYSIS, null);
    try {
      const response = await WhitelabelAPI.analyzeScript(scriptContent);
      commit(types.default.SET_WHITELABEL_ANALYSIS, response.data.analysis);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isAnalyzing: false });
    }
  },

  // Test AI connection
  testAiConnection: async ({ commit }, { apiKey, model }) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isTestingConnection: true });
    try {
      const response = await WhitelabelAPI.testAiConnection(apiKey, model);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, {
        isTestingConnection: false,
      });
    }
  },

  // Update AI config
  updateAiConfig: async ({ commit, dispatch }, { apiKey, model }) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isSaving: true });
    try {
      const response = await WhitelabelAPI.updateAiConfig(apiKey, model);
      commit(types.default.SET_WHITELABEL_AI_CONFIG, {
        model,
        isConfigured: true,
      });
      await dispatch('fetchCredentialsStatus');
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isSaving: false });
    }
  },

  // Export scripts
  exportScripts: async ({ commit }) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isExporting: true });
    try {
      const response = await WhitelabelAPI.exportScripts();
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `whitelabel-scripts-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isExporting: false });
    }
  },

  // Import scripts
  importScripts: async ({ commit, dispatch }, file) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isImporting: true });
    try {
      await WhitelabelAPI.importScripts(file);
      await dispatch('fetchConfig');
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isImporting: false });
    }
  },

  // Get diff between current and backup
  getDiff: async (_, timestamp) => {
    try {
      const response = await WhitelabelAPI.getDiff(timestamp);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    }
  },

  // Fetch audit logs
  fetchAuditLogs: async ({ commit }) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isFetching: true });
    try {
      const response = await WhitelabelAPI.getAuditLogs();
      commit(types.default.SET_WHITELABEL_AUDIT_LOGS, response.data.logs || []);
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isFetching: false });
    }
  },

  // Fetch credentials status
  fetchCredentialsStatus: async ({ commit }) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isFetching: true });
    try {
      const response = await WhitelabelAPI.getCredentialsStatus();
      commit(types.default.SET_WHITELABEL_CREDENTIALS_STATUS, response.data);
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isFetching: false });
    }
  },

  // Migrate credentials from legacy system
  migrateCredentials: async ({ commit, dispatch }) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isMigrating: true });
    try {
      const response = await WhitelabelAPI.migrateCredentials();
      await dispatch('fetchCredentialsStatus');
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isMigrating: false });
    }
  },

  // Chat with AI assistant
  sendChatMessage: async ({ commit, state: moduleState }, message) => {
    commit(types.default.SET_WHITELABEL_UI_FLAG, { isChatting: true });

    // Add user message to chat
    commit(types.default.ADD_WHITELABEL_CHAT_MESSAGE, {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await WhitelabelAPI.sendChatMessage(message, {
        current_scripts: moduleState.config.scripts,
        chat_history: moduleState.chatMessages.slice(-10), // Last 10 messages
      });

      // Add assistant response to chat
      commit(types.default.ADD_WHITELABEL_CHAT_MESSAGE, {
        role: 'assistant',
        content: response.data.response,
        code_suggestions: response.data.code_suggestions,
        timestamp: new Date().toISOString(),
      });

      return response.data;
    } catch (error) {
      // Add error message to chat
      commit(types.default.ADD_WHITELABEL_CHAT_MESSAGE, {
        role: 'error',
        content: error.response?.data?.error || 'Erro ao processar mensagem',
        timestamp: new Date().toISOString(),
      });
      throw new Error(
        error.response?.data?.message || error.message || 'Erro desconhecido'
      );
    } finally {
      commit(types.default.SET_WHITELABEL_UI_FLAG, { isChatting: false });
    }
  },

  // Clear chat history
  clearChat: ({ commit }) => {
    commit(types.default.CLEAR_WHITELABEL_CHAT);
  },
};

export const mutations = {
  [types.default.SET_WHITELABEL_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },

  [types.default.SET_WHITELABEL_CONFIG]($state, data) {
    $state.config = {
      scripts: data.whitelabel_scripts || '',
      lastModified: data.updated_at,
      ...data,
    };
    // Also update AI config from response if available
    if (data.ai_configured !== undefined) {
      $state.aiConfig.isConfigured = data.ai_configured;
    }
    if (data.ai_model) {
      $state.aiConfig.model = data.ai_model;
    }
  },

  [types.default.SET_WHITELABEL_SCRIPTS]($state, scripts) {
    $state.config.scripts = scripts;
    $state.config.lastModified = new Date().toISOString();
  },

  [types.default.SET_WHITELABEL_BACKUPS]($state, backups) {
    $state.backups = backups;
  },

  [types.default.SET_WHITELABEL_AUDIT_LOGS]($state, logs) {
    $state.auditLogs = logs;
  },

  [types.default.SET_WHITELABEL_AI_CONFIG]($state, config) {
    $state.aiConfig = {
      ...$state.aiConfig,
      ...config,
    };
  },

  [types.default.SET_WHITELABEL_CREDENTIALS_STATUS]($state, status) {
    // API returns { whitelabel: {...}, captain: {...}, encryption_enabled: bool }
    const whitelabel = status.whitelabel || status;
    $state.credentialsStatus = {
      configured: whitelabel.configured || false,
      provider: whitelabel.provider,
      model: whitelabel.model,
      hasEncryptedKey: !whitelabel.using_legacy && whitelabel.configured,
      hasLegacyKey: whitelabel.using_legacy || false,
      maskedKey: whitelabel.masked_key,
      validationStatus: whitelabel.validation_status,
      encryptionEnabled: status.encryption_enabled,
    };
  },

  [types.default.SET_WHITELABEL_ANALYSIS]($state, analysis) {
    $state.analysis = analysis;
  },

  [types.default.ADD_WHITELABEL_CHAT_MESSAGE]($state, message) {
    $state.chatMessages.push(message);
  },

  [types.default.CLEAR_WHITELABEL_CHAT]($state) {
    $state.chatMessages = [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
